// Types mirroring docs/api.md (snake_case JSON).

export type UUID = string;
export type ISODate = string;

export type StatusCategory = "backlog" | "todo" | "in_progress" | "blocked" | "done" | "cancelled";
export const STATUS_CATEGORIES: StatusCategory[] = [
  "backlog",
  "todo",
  "in_progress",
  "blocked",
  "done",
  "cancelled",
];
export type Priority = 0 | 1 | 2 | 3 | 4;
export type BindingMode = "mirror" | "push_only" | "pull_only" | "remote_authoritative";
export const BINDING_MODES: BindingMode[] = ["mirror", "push_only", "pull_only", "remote_authoritative"];

export interface Organization {
  id: UUID;
  slug: string;
  name: string;
}

export type ActorKind = "member" | "guest" | "ghost" | "remote" | "bot";
export interface Actor {
  id: UUID;
  org_id: UUID;
  kind: ActorKind;
  account_id: UUID | null;
  display_name: string;
  primary_email: string | null;
  avatar_url: string | null;
  /** v0.2 (admin members list only): actor merged into another one. */
  merged_into?: UUID | null;
}

export type OrgRole = string; // "owner" | "admin" | "member" | ... (server-defined)

export interface Me {
  /** v0.2: timezone + display_name (optional for older servers). */
  account: { id: UUID; email: string; timezone?: string; display_name?: string };
  orgs: { org: Organization; role: OrgRole; actor: Actor }[];
}

export interface Instance {
  mode: "personal" | "organization" | "hosted";
  needs_setup: boolean;
  name: string;
}

export interface Space {
  id: UUID;
  org_id: UUID;
  name: string;
  /** v0.3: set when archived (optional for older servers). */
  archived_at?: ISODate | null;
}

export interface Status {
  id: UUID;
  scheme_id: UUID;
  name: string;
  category: StatusCategory;
  color: string | null;
  position: number;
}

export type CollectionKind = "list" | "board" | "project" | "notebook" | "queue";
export interface Collection {
  id: UUID;
  space_id: UUID;
  kind: CollectionKind;
  name: string;
  status_scheme_id: UUID;
  /** v0.3: set when archived (optional for older servers). */
  archived_at?: ISODate | null;
  /** v0.4: buckets (named columns independent of status) are enabled (optional for older servers). */
  buckets_enabled?: boolean;
}

export interface BindingSummary {
  id: UUID;
  connector_type: string;
  remote_collection_name: string;
  mode: BindingMode;
}

export interface CollectionSummary extends Collection {
  open_count: number;
  bindings: BindingSummary[];
}

export type ViewType = "board" | "list" | "calendar" | "timeline";
export interface View {
  id: UUID;
  collection_id: UUID;
  name: string;
  type: ViewType;
  group_by: string;
  filter: Record<string, unknown> | null;
  sort: Record<string, unknown>[] | null;
}

export interface StatusMapEntry {
  remote_status_id: string;
  remote_name: string;
  category: StatusCategory;
  local_status_id: UUID | null;
}

export interface Binding {
  id: UUID;
  collection_id: UUID;
  connection_id: UUID;
  connector_type: string;
  remote_collection_id: string;
  remote_collection_name: string;
  mode: BindingMode;
  status_map: StatusMapEntry[];
  last_synced_at: ISODate | null;
  last_error: string | null;
}

export interface CollectionDetail {
  collection: Collection;
  statuses: Status[];
  views: View[];
  bindings: Binding[];
}

export type SyncState = "ok" | "pending_create" | "conflict" | "error";
export interface ItemSyncInfo {
  binding_id: UUID;
  connector_type: string;
  remote_id: string | null;
  remote_url: string | null;
  state: SyncState;
  last_error: string | null;
  /** v0.2: which connection was used for the last push. */
  acted_as?: "user" | "binding" | null;
}

export type ItemType = "task" | "note" | "meeting";
export interface Item {
  id: UUID;
  uid: string;
  space_id: UUID;
  collection_id: UUID;
  type: ItemType;
  title: string;
  body_doc_id: UUID | null;
  status_id: UUID;
  status_category: StatusCategory;
  priority: Priority;
  start_at: ISODate | null;
  due_at: ISODate | null;
  due_has_time: boolean;
  completed_at: ISODate | null;
  recurrence: string | null;
  parent_id: UUID | null;
  position: string;
  created_by: UUID;
  assignees: UUID[];
  labels: string[];
  fields: Record<string, unknown>;
  ext: Record<string, unknown>;
  authority: string;
  version: number;
  created_at: ISODate;
  updated_at: ISODate;
  sync: ItemSyncInfo[];
  /** v0.4: bucket of the item (optional for older servers). */
  bucket_id?: UUID | null;
}

export interface Meeting {
  item_id: UUID;
  starts_at: ISODate | null;
  ends_at: ISODate | null;
  location: string | null;
  online_url: string | null;
  /** v0.3: collection that top-level task lines in the notes go to (fallback: the meeting's collection). */
  task_collection_id?: UUID | null;
}

export interface Relation {
  type: string;
  direction: "out" | "in";
  item: Item;
}

export interface ItemDetail {
  item: Item;
  body: string;
  meeting: Meeting | null;
  children: Item[];
  relations: Relation[];
}

export interface Comment {
  id: UUID;
  item_id: UUID;
  author_id: UUID;
  body: string;
  kind: string;
  created_at: ISODate;
}

export interface CreateItem {
  type?: ItemType;
  title: string;
  body?: string;
  status_id?: UUID;
  priority?: Priority;
  start_at?: ISODate | null;
  due_at?: ISODate | null;
  due_has_time?: boolean;
  assignees?: UUID[];
  labels?: string[];
  parent_id?: UUID;
  meeting?: Partial<Omit<Meeting, "item_id">>;
  /** v0.4 */
  bucket_id?: UUID | null;
}

export interface UpdateItem {
  title?: string;
  status_id?: UUID;
  priority?: Priority;
  start_at?: ISODate | null;
  due_at?: ISODate | null;
  due_has_time?: boolean;
  assignees?: UUID[];
  labels?: string[];
  fields?: Record<string, unknown>;
  recurrence?: string | null;
  body?: string;
  meeting?: Partial<Omit<Meeting, "item_id">>;
  /** v0.4: server picks the first status of that category in the item's scheme. */
  status_category?: StatusCategory;
  /** v0.4 */
  bucket_id?: UUID | null;
}

export interface DocumentDto {
  id: UUID;
  item_id: UUID | null;
  markdown: string;
  updated_at: ISODate;
}

export type ConnectionHealth = "ok" | "error" | "needs_auth" | "unknown";
export interface Connection {
  id: UUID;
  org_id: UUID;
  connector_type: string;
  scope: "org" | "user";
  owner_actor_id: UUID;
  name: string;
  remote_tenant: string | null;
  config: Record<string, unknown>;
  health: ConnectionHealth;
  last_error: string | null;
}

/** Minimal JSON-schema subset used to render connector forms. */
export interface JsonSchemaProp {
  type?: "string" | "boolean" | "integer" | "number";
  title?: string;
  description?: string;
  format?: string;
  enum?: (string | number)[];
  default?: unknown;
}
export interface JsonSchemaObject {
  type?: "object";
  properties?: Record<string, JsonSchemaProp>;
  required?: string[];
}

/**
 * Connector manifest (from the connector SDK). Only `connector_type` is relied on;
 * the rest is used when present. ASSUMPTION: field names below (see report).
 */
export interface Manifest {
  connector_type: string;
  name?: string;
  display_name?: string;
  description?: string;
  auth?: string | string[]; // e.g. "basic" | "api_key" | "oauth2"
  auth_kinds?: string[];
  capabilities?: Record<string, unknown> | string[];
  config_schema?: JsonSchemaObject | null;
  credential_schema?: JsonSchemaObject | null;
  [key: string]: unknown;
}

export interface RemoteCollection {
  remote_id: string;
  name: string;
  kind: string;
  url: string | null;
  bound_collection_id: UUID | null;
}

export interface ExternalIdentity {
  id: UUID;
  org_id: UUID;
  connector_type: string;
  remote_tenant: string | null;
  remote_user_id: string;
  handle: string | null;
  email: string | null;
  display_name: string | null;
  actor_id: UUID | null;
  match_method: string;
}

export interface SyncCounts {
  created: number;
  updated: number;
  deleted: number;
}
export interface SyncReport {
  binding_id: UUID;
  pulled: SyncCounts;
  pushed: SyncCounts;
  conflicts: number;
  errors: string[];
  finished_at: ISODate;
}

export interface Member {
  actor: Actor;
  role: OrgRole | null;
}

export type ServerEvent =
  | { type: "item.changed"; item: Item }
  | { type: "item.deleted"; item_id: UUID; collection_id: UUID }
  | { type: "collection.changed"; collection_id: UUID }
  | { type: "sync.finished"; report: SyncReport };

// ---------------------------------------------------------------------------
// v0.2: admin panel

export interface OrgSettings {
  name: string;
  default_timezone: string;
  open_registration: boolean;
  allow_user_connections: boolean;
  /** null = all connectors allowed */
  allowed_connectors: string[] | null;
}

export interface AdminOverview {
  org: Organization;
  instance: { mode: string; name: string; version: string };
  counts: {
    members: number;
    guests: number;
    ghosts: number;
    spaces: number;
    collections: number;
    items: number;
    connections: number;
    bindings: number;
  };
  health: { connections_needing_auth: number; connections_error: number; bindings_error: number };
  settings: OrgSettings;
}

export type AccountStatus = "active" | "suspended";
export interface AdminMember {
  actor: Actor;
  role: OrgRole | null;
  account: { id: UUID; email: string; status: AccountStatus; last_login_at: ISODate | null } | null;
  connections: number;
  identities: number;
}

export type MatchOn = "email" | "username" | "attribute";
export type RuleAction = "link" | "create_ghost" | "ignore";
export interface MappingRule {
  id: UUID;
  priority: number;
  applies_to: string;
  match_on: MatchOn;
  attribute: string | null;
  transform: { lowercase?: boolean; strip_domain?: boolean; alias_domains?: string[] };
  action: RuleAction;
}

export interface AdminBinding extends Binding {
  collection_name: string;
  space_id: UUID;
  connection_name: string;
  connection_scope: "org" | "user";
  connection_health: ConnectionHealth;
}

export interface AuditEntry {
  id: string;
  created_at: ISODate;
  /** id is null for system/sync operations (display_name e.g. "Sync: deck Ops board" or "System"). */
  actor: { id: UUID | null; display_name: string } | null;
  kind: string;
  path: string;
  item_id: UUID | null;
  item_title: string | null;
  origin: Record<string, unknown> | null;
}

export interface RuleRunResult {
  linked: number;
  ghosts_created: number;
  unmatched: number;
}

// ---------------------------------------------------------------------------
// v0.3: archive / delete, document anchors

export type RemoteArchiveOutcome = "archived" | "unarchived" | "unsupported" | "skipped" | "error";
export interface RemoteArchiveResult {
  binding_id: UUID;
  connector_type: string;
  result: RemoteArchiveOutcome;
  error: string | null;
}
export interface ArchiveResult {
  collection: CollectionDetail;
  remote: RemoteArchiveResult[];
}
export interface SpaceArchiveResult {
  space: Space;
  collections: ArchiveResult[];
}

/** ASSUMPTION (not in docs/api.md): anchor → item mapping of a document. */
export interface DocumentAnchor {
  anchor: string;
  item_id: UUID;
}

// ---------------------------------------------------------------------------
// v0.4: space overview, buckets

/** Collection info returned with the space overview items. */
export interface SpaceCollectionInfo {
  id: UUID;
  name: string;
  kind: CollectionKind;
  buckets_enabled: boolean;
}

export interface SpaceItems {
  items: Item[];
  collections: SpaceCollectionInfo[];
  /** Every status referenced by the space's collections. */
  statuses: Status[];
}

export interface BucketRemote {
  binding_id: UUID;
  connector_type: string;
  /** Deck stack id / To Do list id. */
  remote_id: string;
}

export interface Bucket {
  id: UUID;
  collection_id: UUID;
  name: string;
  position: number;
  remote: BucketRemote[];
  /** To Do: the binding whose list IS this bucket (null otherwise). */
  binding_id: UUID | null;
}

/** Target of POST /api/items/{id}/move: a status, a status category (space board) and/or a bucket. */
export interface MoveItemBody {
  status_id?: UUID;
  status_category?: StatusCategory;
  bucket_id?: UUID | null;
  before_id?: UUID | null;
  after_id?: UUID | null;
}
